import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useReference } from "@/lib/api";
import { sentence } from "@/concepts/editorial";
import { ConceptBar, CommandPalette, useConceptData, useCandidate, useSimulated, useHotkeys, useSelection, useVirtual, todayItems, filterRows, actionsFor, GROUP_LABEL, GROUP_ORDER, STATUS_LABEL, READY_LABEL, rel, fmt } from "@/concepts/shared";

const D = "expressive";
const SCREENS = ["today", "workbench", "triage"];
const OUT_TEXT = { supported: "met", not_met: "not met", unknown: "unknown" };
const GROUP_MARK = { strong: "var(--green)", possible: "#5E8A72", needs_information: "var(--ochre-fill)", low_priority: "#B9B3A5", excluded: "var(--clay)" };
const PIPE = [["queued", "#C9C2B0"], ["pending_review", "#9DAE9F"], ["contacted", "#5E8A72"], ["pre_screening", "#2F6650"], ["ready_for_first_visit", "var(--green-deep)"]];

const reqCount = (c) => (c.age_min != null || c.age_max != null ? 1 : 0) + (c.sex && c.sex !== "any" ? 1 : 0) + c.required_conditions.length
  + (c.required_medications.length ? 1 : 0) + (c.smoking_statuses.length ? 1 : 0) + (c.min_pack_years != null ? 1 : 0);

export const Tick = ({ outcome, exclusion, label, size = 11 }) => {
  const fill = outcome === "supported" ? "bg-[color:var(--green)]" : outcome === "not_met" ? "bg-[color:var(--clay)]" : "border-[1.5px] border-dashed border-[color:var(--ochre)] bg-transparent";
  return (
    <span role="img" aria-label={`${label || "criterion"}: ${OUT_TEXT[outcome]}${exclusion ? " (exclusion rule)" : ""}`} title={`${label || ""} — ${OUT_TEXT[outcome]}${exclusion ? " · exclusion" : ""}`}
      className={`inline-flex shrink-0 items-center justify-center ${fill} ${exclusion ? "rotate-45" : "rounded-[2px]"}`} style={{ width: exclusion ? size - 2 : size, height: exclusion ? size - 2 : size }}>
      {outcome === "not_met" && <span className={`block h-[1.5px] w-[70%] bg-white ${exclusion ? "" : "rotate-45"}`} />}
    </span>
  );
};

const Stamp = ({ tone = "ink", children, testId }) => {
  const t = { ink: "text-[color:var(--ink)] border-[color:var(--ink)]", green: "text-[color:var(--green)] border-[color:var(--green)]", clay: "text-[color:var(--clay)] border-[color:var(--clay)]", ochre: "text-[color:var(--ochre)] border-[color:var(--ochre)]", navy: "text-[color:var(--navy)] border-[color:var(--navy)]", mute: "text-[color:var(--mute)] border-[color:var(--rule)]" }[tone];
  return <span data-testid={testId} className={`mono inline-flex items-center gap-1 whitespace-nowrap border px-1.5 py-[2px] text-[10.5px] font-medium uppercase tracking-[0.08em] ${t}`}>{children}</span>;
};

const readyTone = { ready: "green", restricted: "ochre", blocked: "clay" };

const Seal = ({ permission, restrictions = [] }) => {
  const hold = restrictions.some((r) => r.startsWith("Recruitment hold"));
  const chan = restrictions.some((r) => r.startsWith("No permitted channel"));
  const consent = permission === "Consented" ? "ok" : permission === "Unconfirmed" ? "warn" : "bad";
  const seg = [["Consent", consent, permission], ["Channel", chan ? "warn" : "ok", chan ? "No usable channel" : "Channel available"], ["Hold", hold ? "warn" : "ok", hold ? "Active hold" : "No hold"]];
  const c = { ok: "bg-[color:var(--green)]", warn: "bg-[color:var(--ochre-fill)]", bad: "bg-[color:var(--clay)]" };
  return (
    <span className="flex items-center gap-2" aria-label={seg.map((s) => s[2]).join(", ")}>
      <span className="flex gap-[2px]">{seg.map(([k, s, t]) => <span key={k} title={`${k}: ${t}`} className={`h-[14px] w-[5px] ${c[s]}`} />)}</span>
      <span className={`text-[12px] ${consent === "ok" ? "text-[color:var(--ink2)]" : consent === "warn" ? "text-[color:var(--ochre)]" : "text-[color:var(--clay)]"}`}>{permission}</span>
    </span>
  );
};

const GroupMark = ({ group }) => (
  <span className="flex items-center gap-2 text-[13px]"><span className="h-3.5 w-[3px]" style={{ background: GROUP_MARK[group] }} /><span className={group === "excluded" ? "text-[color:var(--clay)]" : ""}>{GROUP_LABEL[group]}</span></span>
);

const Shell = ({ active, crumbs, onSearch, children, counts }) => (
  <div className="c-xp flex h-screen" data-testid={`xp-${active}`}>
    <aside className="hidden w-[228px] shrink-0 flex-col bg-[color:var(--ink)] px-4 py-5 text-[#EDE9DD] lg:flex">
      <Link to="/concepts" className="block leading-none" data-testid="xp-logo">
        <span className="mono block text-[9.5px] tracking-[0.38em] text-[#A9A493]">AVENIR</span>
        <span className="serif text-[36px]">align<span className="text-[color:var(--lime)]">.</span></span>
      </Link>
      <div className="mt-5 border border-white/15 px-3 py-2"><span className="mono block text-[10px] uppercase tracking-[0.14em] text-[#A9A493]">Workspace</span><span className="text-[13px]">Clinical research · QASMC</span></div>
      <nav className="mt-6 space-y-0.5" aria-label="Primary">
        {[["01", "Today", "today", counts?.today], ["02", "Workbench", "workbench", counts?.studies], ["03", "Focus review", "triage", null]].map(([n, l, s, c]) => (
          <Link key={s} to={`/concepts/${D}/${s}`} data-testid={`xp-nav-${s}`} className={`flex items-baseline gap-3 px-2 py-2 text-[14px] ${active === s ? "bg-white/[0.07] shadow-[inset_3px_0_0_var(--lime)]" : "text-[#BDB8A8] hover:text-white"}`}>
            <span className="mono text-[10.5px] text-[#8D8877]">{n}</span><span className="flex-1">{l}</span>{c != null && <span className="mono text-[11px] text-[#8D8877]">{c}</span>}
          </Link>
        ))}
        <span className="flex items-baseline gap-3 px-2 py-2 text-[14px] text-[#8D8877]"><span className="mono text-[10.5px]">04</span>Participants</span>
        <p className="mono px-2 pb-1 pt-6 text-[10px] uppercase tracking-[0.16em] text-[#8D8877]">Planned</p>
        {["Communications", "Intake", "Reports"].map((l) => <span key={l} className="block px-2 py-1.5 text-[13px] text-[#77735F]">{l}</span>)}
      </nav>
      <div className="mt-auto border-t border-white/10 pt-3 text-[13px]">Demo Recruiter<span className="mono block text-[10.5px] text-[#8D8877]">RECRUITER · NO AUTH</span></div>
    </aside>
    <div className="flex min-w-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-4 border-b hair px-6">
        <p className="text-[13px] text-[color:var(--mute)]">{crumbs}</p>
        <button onClick={onSearch} className="ml-auto flex w-[340px] items-center gap-2 border-b border-[color:var(--ink)] pb-1 text-left text-[13px] text-[color:var(--mute)]" data-testid="xp-search">
          Search participants, studies, actions<span className="kcap ml-auto">⌘K</span>
        </button>
        <Stamp tone="ochre" testId="xp-synthetic">Synthetic data</Stamp>
      </header>
      {children}
    </div>
  </div>
);

const HORIZONS = [["Needs you now", [0]], ["Due today", [1]], ["Ready to action", [2, 3]], ["Approaching handoff", [4]]];

export const ExpressiveToday = () => {
  const { dash, studies, studyId } = useConceptData();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(0);
  const items = todayItems(dash, D);
  const flat = HORIZONS.flatMap(([, u]) => items.filter((x) => u.includes(x.urgency)).slice(0, 6));
  useHotkeys({ "mod+k": () => setOpen(true), j: () => setCur((i) => Math.min(flat.length - 1, i + 1)), k: () => setCur((i) => Math.max(0, i - 1)), enter: () => flat[cur] && nav(flat[cur].to) });
  if (!dash) return <div className="c-xp min-h-screen p-10">Loading…</div>;
  const m = dash.metrics, next = items[0];
  const d = new Date(dash.today);
  return (
    <Shell active="today" crumbs="Today" onSearch={() => setOpen(true)} counts={{ today: items.length, studies: dash.study_progress.length }}>
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-auto xl:grid-cols-[1fr_360px]">
        <main className="px-10 pb-28 pt-8">
          <p className="label">{d.toLocaleDateString("en-GB", { weekday: "long" })} · recruitment brief · <span className="normal-case tracking-normal">J/K move, ↵ open, ⌘K search</span></p>
          <div className="mt-1 flex flex-wrap items-end justify-between gap-6">
            <h1 className="serif text-[76px] leading-[0.9]">{d.toLocaleDateString("en-GB", { day: "numeric", month: "long" })}</h1>
            <dl className="flex gap-8 pb-2" data-testid="xp-metrics">
              {[["Due", m.followups_due], ["Overdue", m.overdue], ["To review", m.awaiting_review], ["Handoff", m.handoff]].map(([l, n]) => (
                <div key={l}><dt className="label">{l}</dt><dd className={`serif text-[44px] leading-none tnum ${l === "Overdue" && n ? "text-[color:var(--clay)]" : ""}`}>{n}</dd></div>
              ))}
            </dl>
          </div>
          {next && (
            <Link to={next.to} className="c-rise mt-7 grid grid-cols-[1fr_auto] items-end gap-6 bg-[color:var(--ink)] p-7 text-[#F3EFE4] shadow-[inset_0_-4px_0_var(--lime)]" data-testid="xp-next-up">
              <div>
                <p className="mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--lime)]">Next up · {next.kind}</p>
                <p className="serif mt-3 text-[38px] leading-[1.05]">{next.title}</p>
                <p className="mt-2 text-[14px] text-[#C9C4B4]">{next.who} <span className="mono text-[12px]">· {next.ref} · {next.study}</span> · {next.when}</p>
              </div>
              <span className="flex items-center gap-2 bg-[color:var(--lime)] px-4 py-2.5 text-[14px] font-medium text-[color:var(--ink)]">{next.action}<span className="kcap">↵</span></span>
            </Link>
          )}
          {HORIZONS.map(([title, u]) => {
            const list = items.filter((x) => u.includes(x.urgency)).slice(0, 6);
            if (!list.length) return null;
            return (
              <section key={title} className="mt-9" data-testid={`xp-horizon-${u[0]}`}>
                <div className="flex items-baseline justify-between border-b border-[color:var(--ink)] pb-1.5"><h2 className="serif text-[26px]">{title}</h2><span className="label">{u[0] === 0 && m.overdue > list.length ? `${list.length} shown of ${m.overdue} overdue` : `${items.filter((x) => u.includes(x.urgency)).length} items`}</span></div>
                {list.map((x) => {
                  const i = flat.indexOf(x);
                  return (
                    <Link key={x.id} to={x.to} onMouseEnter={() => setCur(i)} data-testid={`xp-agenda-${i}`}
                      className={`group grid grid-cols-[44px_150px_1fr_110px_130px] items-center gap-4 border-b hair py-3 ${cur === i ? "bg-[color:var(--paper)] shadow-[inset_3px_0_0_var(--lime)]" : ""}`}>
                      <span className="serif pl-2 text-[22px] text-[color:var(--mute)] tnum">{String(i + 1).padStart(2, "0")}</span>
                      <span><Stamp tone={x.urgency === 0 ? "clay" : x.urgency === 1 ? "ochre" : "mute"}>{x.kind}</Stamp></span>
                      <span className="min-w-0"><span className="block truncate text-[15px] font-medium">{x.title}</span><span className="text-[13px] text-[color:var(--mute)]">{x.who} <span className="mono text-[12px]">· {x.ref}</span></span></span>
                      <span className="mono text-[12px] text-[color:var(--mute)]">{x.study}</span>
                      <span className="whitespace-nowrap pr-2 text-right text-[12.5px] text-[color:var(--mute)]">{x.when}</span>
                    </Link>
                  );
                })}
              </section>
            );
          })}
        </main>
        <aside className="border-l hair bg-[color:var(--paper)] px-6 pb-28 pt-8" data-testid="xp-pulse">
          <p className="label">Study pipelines</p>
          {dash.study_progress.map((s) => (
            <Link key={s.id} to={`/concepts/${D}/workbench?study=${s.id}`} className="block border-b hair py-4" data-testid={`xp-study-${s.reference}`}>
              <div className="flex items-baseline justify-between"><span className="mono text-[11px] text-[color:var(--mute)]">{s.reference}</span><span className="serif text-[22px] leading-none tnum">{s.pipeline.ready_for_first_visit}<span className="text-[14px] text-[color:var(--mute)]">/{s.target}</span></span></div>
              <p className="mt-0.5 truncate text-[14px] font-medium">{s.name}</p>
              <div className="mt-2.5 flex h-[7px] gap-[2px]">{PIPE.map(([k, c]) => s.pipeline[k] > 0 && <span key={k} title={`${STATUS_LABEL[k]}: ${s.pipeline[k]}`} style={{ flex: s.pipeline[k], background: c }} />)}</div>
            </Link>
          ))}
          <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">{PIPE.map(([k, c]) => <span key={k} className="flex items-center gap-1 text-[11px] text-[color:var(--mute)]"><span className="h-2 w-2" style={{ background: c }} />{STATUS_LABEL[k]}</span>)}</div>
          <p className="label mt-9">Recent activity</p>
          {dash.recent_activity.slice(0, 6).map((a) => <p key={a.id} className="border-b hair py-2.5 text-[13px] leading-snug">{a.summary}<span className="mono block text-[10.5px] text-[color:var(--mute)]">{rel(a.at)} · {a.actor_name}</span></p>)}
        </aside>
      </div>
      <CommandPalette open={open} setOpen={setOpen} dir={D} studies={studies || []} studyId={studyId} contentClass="c-xp rounded-none border-[color:var(--ink)]" />
      <ConceptBar dir={D} screen="today" screens={SCREENS} />
    </Shell>
  );
};

const ModeSwitch = ({ mode, studyId }) => (
  <div className="flex border border-[color:var(--ink)]" role="tablist" aria-label="Workbench mode">
    {[["workbench", "Overview"], ["triage", "Focus review"]].map(([s, l]) => (
      <Link key={s} to={`/concepts/${D}/${s}?study=${studyId}`} role="tab" aria-selected={mode === s} data-testid={`xp-mode-${s}`}
        className={`flex items-center gap-2 px-3 py-1.5 text-[13px] ${mode === s ? "bg-[color:var(--ink)] text-[#F3EFE4]" : "hover:bg-[color:var(--paper)]"}`}>{l}<span className="kcap">T</span></Link>
    ))}
  </div>
);

const GroupIndex = ({ rows, group, setGroup, compact }) => (
  <div className="flex flex-wrap items-end gap-x-6 gap-y-2" role="tablist" aria-label="Match groups">
    {[...(compact ? [] : [["all", "All", rows.length]]), ...GROUP_ORDER.map((g) => [g, GROUP_LABEL[g], rows.filter((r) => r.group === g).length])].map(([k, l, n]) => (
      <button key={k} role="tab" aria-selected={group === k} onClick={() => setGroup(k)} data-testid={`xp-group-${k}`}
        className={`flex items-baseline gap-1.5 border-b-2 pb-1 ${group === k ? "border-[color:var(--ink)]" : "border-transparent text-[color:var(--mute)] hover:text-[color:var(--ink)]"}`}>
        <span className={`serif leading-none tnum ${compact ? "text-[24px]" : "text-[32px]"}`}>{n}</span><span className="text-[13px]">{l}</span>
      </button>
    ))}
  </div>
);

const Verdict = ({ ev, status }) => (
  <p className="serif text-[22px] leading-snug">
    {GROUP_LABEL[ev.group]} match{ev.group === "excluded" ? " — a confirmed exclusion applies" : ""}.{" "}
    <span className={ev.readiness === "ready" ? "text-[color:var(--green)]" : ev.readiness === "blocked" ? "text-[color:var(--clay)]" : "text-[color:var(--ochre)]"}>{READY_LABEL[ev.readiness]}</span>
    {status ? `, ${STATUS_LABEL[status].toLowerCase()} in this study.` : ", not yet in this study."}
  </p>
);

const Restrictions = ({ d }) => {
  const ev = d.evaluation;
  const list = [...ev.restrictions.map((r) => [r.severity === "blocked" ? "clay" : "ochre", r.label, r.severity === "blocked" ? "Blocks outreach" : "Restricts outreach"]), ...ev.conflicts.map((c) => ["navy", c.label, "Study conflict"])];
  if (!list.length) return null;
  return (
    <div className="space-y-1.5 border-l-[3px] border-[color:var(--clay)] bg-[#F4E7DF] px-4 py-3" data-testid="xp-restrictions">
      {list.map(([tone, label, kind]) => <p key={label} className="flex items-start gap-2 text-[13px]"><Stamp tone={tone}>{kind}</Stamp><span className="pt-[1px]">{label}</span></p>)}
    </div>
  );
};

const ActionLedger = ({ d, pid, run, keys }) => {
  const { primary, others } = actionsFor(d);
  return (
    <div data-testid="xp-actions">
      {primary && (
        <button onClick={() => run(pid, primary)} className="flex w-full items-center justify-between bg-[color:var(--ink)] px-5 py-4 text-left text-[15px] font-medium text-[#F3EFE4] shadow-[inset_0_-3px_0_var(--lime)] hover:bg-[color:var(--green-deep)]" data-testid="xp-primary-action">
          {primary.label}<span className="kcap text-[color:var(--lime)]">{keys ? "A" : "↵"}</span>
        </button>
      )}
      <ul className="mt-1">
        {others.map((a, i) => (
          <li key={a.key}>
            <button disabled={!a.enabled} onClick={() => run(pid, a)} data-testid={`xp-action-${a.key}`}
              className="grid w-full grid-cols-[1fr_auto] items-center gap-2 border-b hair px-1 py-2.5 text-left text-[13.5px] enabled:hover:bg-[color:var(--paper)] disabled:cursor-not-allowed">
              <span className={a.enabled ? "" : "text-[color:var(--mute)]"}>{a.label}{!a.enabled && <span className="block text-[12px] leading-snug text-[color:var(--mute)]">Unavailable — {a.reason}</span>}</span>
              {keys && <span className={`kcap ${a.enabled ? "" : "opacity-30"}`}>{i + 1}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const EvidenceColumns = ({ ev, cols = 1 }) => (
  <div className={`grid gap-6 ${cols === 3 ? "md:grid-cols-3" : ""}`} data-testid="xp-evidence">
    {[["required", "Required"], ["exclusion", "Exclusion rules"], ["desirable", "Desirable"]].map(([k, t]) => {
      const list = ev.checks.filter((c) => c.kind === k);
      if (!list.length) return null;
      return (
        <div key={k}>
          <p className="label mb-2 border-b border-[color:var(--ink)] pb-1">{t} · {list.filter((c) => c.outcome === "supported").length}/{list.length} {k === "exclusion" ? "clear" : "met"}</p>
          {list.map((c) => (
            <div key={c.key + k} className="flex gap-2.5 py-1.5">
              <span className="pt-1"><Tick outcome={c.outcome} exclusion={k === "exclusion"} label={c.short || c.label} /></span>
              <span className="text-[13px] leading-snug"><b className="font-medium">{c.short || c.label}</b>{" "}
                <span className={`mono text-[10.5px] uppercase ${c.outcome === "not_met" ? "text-[color:var(--clay)]" : c.outcome === "unknown" ? "text-[color:var(--ochre)]" : "text-[color:var(--green)]"}`}>{k === "exclusion" ? { supported: "clear", not_met: "triggered", unknown: "unconfirmed" }[c.outcome] : OUT_TEXT[c.outcome]}</span>
                <span className="block text-[color:var(--mute)]">{c.detail}</span></span>
            </div>
          ))}
        </div>
      );
    })}
  </div>
);

const Lanes = ({ d }) => {
  const lanes = [...(d.relationship ? [{ ...d.relationship, study_ref: d.study.reference, current: true }] : []), ...d.other_relationships];
  if (!lanes.length) return <p className="text-[13px] text-[color:var(--mute)]">Not in any study yet.</p>;
  const now = Date.now(), start = Math.min(...lanes.map((l) => new Date(l.added_at).getTime())) - 2 * 86400000;
  const pct = (t) => ((new Date(t).getTime() - start) / (now - start)) * 100;
  return (
    <div className="space-y-2" data-testid="xp-lanes">
      {lanes.map((l) => (
        <div key={l.id} className="grid grid-cols-[96px_1fr] items-center gap-2">
          <span className={`mono truncate text-[11px] ${l.current ? "font-medium" : "text-[color:var(--mute)]"}`}>{l.study_ref}</span>
          <div className="relative h-[18px] bg-[color:var(--ivory)]">
            <span className="absolute top-0 flex h-full items-center truncate px-1.5 text-[10.5px] text-white" style={{ left: `${pct(l.added_at)}%`, right: 0, background: l.status === "unsuitable" ? "#9B9584" : l.current ? "var(--green)" : "var(--navy)" }}>{STATUS_LABEL[l.status]}</span>
          </div>
        </div>
      ))}
      <p className="mono flex justify-between pl-[104px] text-[10px] text-[color:var(--mute)]"><span>{fmt(new Date(start).toISOString())}</span><span>Today</span></p>
    </div>
  );
};

const Inspector = ({ studyId, pid, over, run }) => {
  const { data: d } = useCandidate(studyId, pid);
  if (!d) return <div className="p-7 text-[13px] text-[color:var(--mute)]">Loading…</div>;
  const p = d.participant;
  return (
    <div className="c-slide space-y-5 px-6 py-6" key={pid} data-testid="xp-inspector">
      <div>
        <p className="mono text-[11.5px] text-[color:var(--mute)]">{p.participant_ref} · {p.age ?? "age ?"} · {p.sex || "sex not recorded"} · {p.area}</p>
        <h2 className="serif text-[38px] leading-[1]">{p.first_name} {p.last_name}</h2>
      </div>
      <Verdict ev={d.evaluation} status={over[pid] || d.relationship?.status} />
      <Restrictions d={d} />
      <ActionLedger d={d} pid={pid} run={run} />
      <EvidenceColumns ev={d.evaluation} />
      <div><p className="label mb-2 border-b border-[color:var(--ink)] pb-1">Study relationships</p><Lanes d={d} /></div>
    </div>
  );
};

const useWorkbench = (initialGroup) => {
  const ctx = useConceptData();
  const [group, setGroup] = useState(initialGroup);
  const [ready, setReady] = useState("all");
  const [q, setQ] = useState("");
  const rows = useMemo(() => filterRows(ctx.wb?.rows || [], { group, q }).filter((r) => ready === "all" || r.readiness === ready), [ctx.wb, group, q, ready]);
  return { ...ctx, group, setGroup, ready, setReady, q, setQ, rows };
};

const TEMPLATE = (n) => `52px minmax(200px,1.3fr) 128px ${n * 17 + 6}px 160px 150px 130px minmax(170px,1fr)`;

export const ExpressiveWorkbench = () => {
  const w = useWorkbench("all");
  const { data: ref } = useReference();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { over, run } = useSimulated();
  const sel = useSelection(w.rows);
  const box = useRef(null);
  const search = useRef(null);
  const v = useVirtual(w.rows.length, 50, box);
  const { data: cand } = useCandidate(w.studyId, sel.pid);
  const step = (n) => { sel.move(n); v.reveal(Math.min(w.rows.length - 1, Math.max(0, sel.idx + n))); };
  useHotkeys({ "mod+k": () => setOpen(true), j: () => step(1), k: () => step(-1), arrowdown: () => step(1), arrowup: () => step(-1), "/": () => search.current?.focus(), enter: () => run(sel.pid, actionsFor(cand).primary), t: () => nav(`/concepts/${D}/triage?study=${w.studyId}`) });
  if (!w.wb) return <div className="c-xp min-h-screen p-10">Loading…</div>;
  const s = w.wb.study, nReq = reqCount(s.criteria), n = w.wb.rows[0]?.evidence.length || 0;
  return (
    <Shell active="workbench" crumbs={<>Studies / <span className="text-[color:var(--ink)]">{s.reference}</span> / Overview</>} onSearch={() => setOpen(true)}>
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_450px]">
        <div className="flex min-h-0 flex-col">
          <div className="px-8 pb-4 pt-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="label">{s.reference} · {s.therapeutic_area} · {s.phase} · {w.wb.run?.pool_size} in pool of {w.wb.run?.database_size?.toLocaleString()}</p>
                <h1 className="serif mt-1 text-[46px] leading-[1]">{s.name}</h1>
              </div>
              <div className="flex items-center gap-5"><p className="text-right"><span className="serif text-[34px] leading-none tnum">{w.wb.pipeline.ready_for_first_visit}<span className="text-[18px] text-[color:var(--mute)]">/{s.target}</span></span><span className="label block">ready for first visit</span></p><ModeSwitch mode="workbench" studyId={w.studyId} /></div>
            </div>
            <p className="mt-3 max-w-5xl text-[15px] leading-relaxed" data-testid="xp-criteria-sentence">
              {sentence(s.criteria, ref).map(([t, k], i) => <span key={i} className={k === 1 ? "border-b-[1.5px] border-[color:var(--green)] font-medium" : k === 2 ? "border-b-[1.5px] border-[color:var(--clay)] font-medium" : ""}>{t}</span>)}
            </p>
            <div className="mt-5 flex flex-wrap items-end justify-between gap-4 border-b border-[color:var(--ink)]">
              <GroupIndex rows={w.wb.rows} group={w.group} setGroup={w.setGroup} />
              <div className="flex items-center gap-3 pb-2">
                <div className="flex text-[12.5px]" role="group" aria-label="Contactability">
                  {["all", "ready", "restricted", "blocked"].map((r) => <button key={r} onClick={() => w.setReady(r)} aria-pressed={w.ready === r} data-testid={`xp-ready-${r}`} className={`border border-[color:var(--ink)] px-2.5 py-1 capitalize [&:not(:first-child)]:-ml-px ${w.ready === r ? "bg-[color:var(--ink)] text-[#F3EFE4]" : ""}`}>{r === "all" ? "Any contact" : r}</button>)}
                </div>
                <input ref={search} value={w.q} onChange={(e) => w.setQ(e.target.value)} placeholder="Filter name or ID  /" aria-label="Filter candidates" className="w-52 border-b border-[color:var(--rule)] bg-transparent py-1 text-[13px] outline-none placeholder:text-[color:var(--mute)] focus:border-[color:var(--ink)]" data-testid="xp-filter" />
              </div>
            </div>
          </div>
          <div className="mx-8 grid border-b hair pb-2 text-[color:var(--mute)]" style={{ gridTemplateColumns: TEMPLATE(n) }}>
            {["Rank", "Candidate", "Match", "Evidence", "Consent", "Contactability", "Status", "Next action"].map((h) => <span key={h} className="label">{h}</span>)}
          </div>
          <div ref={box} className="min-h-0 flex-1 overflow-auto px-8" data-testid="xp-table">
            <div style={{ height: v.total, position: "relative" }}>
              {w.rows.slice(v.start, v.end).map((r, i) => {
                const on = r.participant_id === sel.pid, st = over[r.participant_id] || r.status;
                return (
                  <button key={r.participant_id} onClick={() => sel.select(r.participant_id)} aria-selected={on} data-testid={`xp-row-${r.participant_ref}`}
                    className={`absolute left-0 right-0 grid h-[50px] items-center border-b hair text-left ${on ? "bg-[color:var(--paper)] shadow-[inset_4px_0_0_var(--lime),inset_0_0_0_1px_var(--ink)]" : "hover:bg-[color:var(--paper)]/70"}`}
                    style={{ top: v.top + i * 50, gridTemplateColumns: TEMPLATE(n) }}>
                    <span className="serif pl-2 text-[19px] text-[color:var(--mute)] tnum">{r.rank}</span>
                    <span className="min-w-0"><span className="block truncate text-[14px] font-medium">{r.name}</span><span className="mono text-[11px] text-[color:var(--mute)]">{r.participant_ref} · {r.age ?? "?"}{r.other_studies.length > 0 && <span className="ml-1.5 text-[color:var(--navy)]">◆ +{r.other_studies.length} study</span>}</span></span>
                    <GroupMark group={r.group} />
                    <span className="flex items-center gap-[6px]">{r.evidence.map((e, ci) => <Tick key={ci} outcome={e.outcome} exclusion={ci >= nReq} label={e.label} />)}</span>
                    <Seal permission={r.permission} restrictions={r.restrictions} />
                    <span><Stamp tone={readyTone[r.readiness]}>{READY_LABEL[r.readiness]}</Stamp></span>
                    <span className="text-[13px]">{st ? STATUS_LABEL[st] : <span className="text-[color:var(--mute)]">Not in study</span>}</span>
                    <span className="truncate pr-2 text-[13px] text-[color:var(--ink2)]">{r.next_action}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mono flex flex-wrap items-center gap-x-5 gap-y-1 border-t hair px-8 py-2.5 text-[11px] text-[color:var(--mute)]" data-testid="xp-legend">
            <span className="flex items-center gap-1.5"><Tick outcome="supported" label="legend" /> met</span>
            <span className="flex items-center gap-1.5"><Tick outcome="not_met" label="legend" /> confirmed not met</span>
            <span className="flex items-center gap-1.5"><Tick outcome="unknown" label="legend" /> unknown — never a failure</span>
            <span className="flex items-center gap-1.5"><Tick outcome="supported" exclusion label="legend" /> exclusion rule (◆ shape)</span>
            <span>Consent · channel · hold</span><span className="ml-auto">J/K move · ↵ primary · T focus review · / filter · ⌘K</span>
          </div>
        </div>
        <aside className="min-h-0 overflow-auto border-l border-[color:var(--ink)] bg-[color:var(--paper)]">{sel.pid && w.rows.length > 0 && <Inspector studyId={w.studyId} pid={sel.pid} over={over} run={run} />}</aside>
      </div>
      <CommandPalette open={open} setOpen={setOpen} dir={D} rows={w.wb.rows} studies={w.studies || []} studyId={w.studyId} contentClass="c-xp rounded-none border-[color:var(--ink)]" />
      <ConceptBar dir={D} screen="workbench" screens={SCREENS} />
    </Shell>
  );
};

export const ExpressiveTriage = () => {
  const w = useWorkbench("strong");
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState({});
  const [log, setLog] = useState([]);
  const sim = useSimulated();
  const sel = useSelection(w.rows);
  const box = useRef(null);
  const v = useVirtual(w.rows.length, 60, box);
  const { data: d } = useCandidate(w.studyId, sel.pid);
  const step = (n) => { sel.move(n); v.reveal(Math.min(w.rows.length - 1, Math.max(0, sel.idx + n))); };
  const decide = (a, label) => {
    if (!sel.pid) return;
    if (a) sim.run(sel.pid, a);
    const r = w.rows[sel.idx];
    setDone((x) => ({ ...x, [sel.pid]: label || a?.label }));
    setLog((l) => [{ id: `${sel.pid}-${l.length}`, who: r?.name, what: label || a?.label }, ...l].slice(0, 6));
    step(1);
  };
  const { primary, others } = actionsFor(d);
  useHotkeys({
    "mod+k": () => setOpen(true), j: () => step(1), k: () => step(-1), arrowdown: () => step(1), arrowup: () => step(-1),
    a: () => primary && decide(primary), x: () => decide(null, "Skipped"), t: () => nav(`/concepts/${D}/workbench?study=${w.studyId}&p=${sel.pid}`),
    ...Object.fromEntries(others.map((a, i) => [String(i + 1), () => a.enabled && decide(a)])),
  });
  if (!w.wb) return <div className="c-xp min-h-screen p-10">Loading…</div>;
  const s = w.wb.study, nReq = reqCount(s.criteria);
  const reviewed = w.rows.filter((r) => done[r.participant_id]).length;
  return (
    <Shell active="triage" crumbs={<>Studies / <span className="text-[color:var(--ink)]">{s.reference}</span> / Focus review</>} onSearch={() => setOpen(true)}>
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[color:var(--ink)] px-8 pb-3 pt-5">
        <div><p className="label">{s.reference} · focus review</p><h1 className="serif text-[34px] leading-none">{s.name}</h1></div>
        <GroupIndex rows={w.wb.rows} group={w.group} setGroup={w.setGroup} compact />
        <div className="flex items-center gap-5">
          <div className="w-44" data-testid="xp-progress"><p className="label">Reviewed {reviewed} of {w.rows.length}</p><div className="mt-1 h-[5px] bg-[color:var(--paper)]"><div className="h-full bg-[color:var(--green)]" style={{ width: `${w.rows.length ? (reviewed / w.rows.length) * 100 : 0}%` }} /></div></div>
          <ModeSwitch mode="triage" studyId={w.studyId} />
        </div>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[280px_1fr_330px]">
        <div ref={box} className="hidden min-h-0 overflow-auto border-r hair lg:block" data-testid="xp-queue">
          <div style={{ height: v.total, position: "relative" }}>
            {w.rows.slice(v.start, v.end).map((r, i) => {
              const on = r.participant_id === sel.pid, dn = done[r.participant_id];
              return (
                <button key={r.participant_id} onClick={() => sel.select(r.participant_id)} aria-current={on} data-testid={`xp-queue-${r.participant_ref}`}
                  className={`absolute left-0 right-0 flex h-[60px] flex-col justify-center gap-1.5 border-b hair px-4 text-left ${on ? "bg-[color:var(--paper)] shadow-[inset_4px_0_0_var(--lime),inset_0_0_0_1.5px_var(--ink)]" : dn ? "opacity-50" : "hover:bg-[color:var(--paper)]"}`} style={{ top: v.top + i * 60 }}>
                  <span className="flex items-baseline justify-between text-[14px] font-medium"><span className="truncate">{dn ? "✓ " : ""}{r.name}</span><span className="mono text-[10.5px] opacity-60">#{r.rank}</span></span>
                  <span className="flex items-center gap-[5px]">{r.evidence.map((e, ci) => <Tick key={ci} outcome={e.outcome} exclusion={ci >= nReq} label={e.label} size={9} />)}<span className="mono ml-auto text-[10px] uppercase opacity-70">{dn || READY_LABEL[r.readiness]}</span></span>
                </button>
              );
            })}
          </div>
        </div>
        <main className="min-h-0 overflow-auto px-10 pb-28 pt-7" data-testid="xp-sheet">
          {!w.rows.length && <p className="serif text-[28px]">No candidates in this group.</p>}
          {d && (
            <div className="c-slide space-y-6" key={sel.pid}>
              <div>
                <p className="label">Candidate {sel.idx + 1} of {w.rows.length} · {d.participant.participant_ref} · {d.participant.age ?? "age ?"} · {d.participant.sex || "sex not recorded"} · last contact {rel(d.participant.last_contact_at)}</p>
                <h2 className="serif mt-1 text-[72px] leading-[0.92]">{d.participant.first_name} {d.participant.last_name}</h2>
              </div>
              <Verdict ev={d.evaluation} status={sim.over[sel.pid] || d.relationship?.status} />
              <Restrictions d={d} />
              {d.evaluation.missing.length > 0 && (
                <div className="border-l-[3px] border-[color:var(--ochre-fill)] bg-[#F6EED8] px-4 py-3 text-[13px]" data-testid="xp-to-confirm">
                  <p className="label mb-1 !text-[color:var(--ochre)]">To confirm before contact</p>{d.evaluation.missing.map((m) => <p key={m}>{m}</p>)}
                </div>
              )}
              <EvidenceColumns ev={d.evaluation} cols={3} />
            </div>
          )}
        </main>
        <aside className="min-h-0 overflow-auto border-l border-[color:var(--ink)] bg-[color:var(--paper)] px-6 py-6">
          <p className="label mb-2">Decide</p>
          {d && <ActionLedger d={d} pid={sel.pid} run={(pid, a) => decide(a)} keys />}
          <button onClick={() => decide(null, "Skipped")} className="mt-2 flex w-full items-center justify-between border border-[color:var(--ink)] px-3 py-2 text-[13.5px] hover:bg-[color:var(--ivory)]" data-testid="xp-skip">Skip for now<span className="kcap">X</span></button>
          <div className="mono mt-6 space-y-1 text-[11px] text-[color:var(--mute)]"><p>J / K — next / previous</p><p>A — primary · 1–6 — alternatives</p><p>T — overview table · ⌘K — search</p></div>
          <p className="label mb-2 mt-8">This session</p>
          {log.length ? log.map((l) => <p key={l.id} className="border-b hair py-1.5 text-[12.5px]"><b className="font-medium">{l.who}</b> — {l.what}</p>) : <p className="text-[12.5px] text-[color:var(--mute)]">No decisions yet. Simulated only.</p>}
        </aside>
      </div>
      <CommandPalette open={open} setOpen={setOpen} dir={D} rows={w.wb.rows} studies={w.studies || []} studyId={w.studyId} contentClass="c-xp rounded-none border-[color:var(--ink)]" />
      <ConceptBar dir={D} screen="triage" screens={SCREENS} />
    </Shell>
  );
};
